<template>
    <div class="gallery-container">

        <div class="gallery-items">
            <div
                v-for="(image, index) in medias"
                :key="index"
                class="gallery-item"
                :class="{'active': currentIndex === index}"
                @click="changeMedia(index)"
            >
                <img
                    v-if="!!image?.thumbnail"
                    :src="image.thumbnail"
                    :alt="`image ${index}`"
                    loading="lazy"
                >
                <img
                    v-else-if="!!image?.poster"
                    :src="image.poster['ads-large'].url"
                    :alt="`image ${index}`"
                    loading="lazy"
                >
            </div>
        </div>

        <div class="main-media">
            <img
                ref="mainImg"
                v-if="!!currentMedia?.large"
                :src="currentMedia.large"
                :alt="`image ${currentIndex}`"
                class="main-img"
                @load="mediaLoaded"
                loading="lazy"
            >
            <video
                ref="mainVideo"
                v-else-if="!!currentMedia?.url"
                :poster="currentMedia.poster['ads-large'].url"
                controls
                controlslist="nofullscreen nodownload noplaybackrate"
                disablepictureinpicture
                preload="metadata"
                class="main-video"
                @error="videoError"
                muted="muted"
                playsinline=""
                loop
                @loadedmetadata="mediaLoaded"
            >
                <source :src="currentMedia.url" type="video/mp4">
                <img
                    :src="currentMedia.poster['ads-large'].url"
                    :alt="`image ${currentIndex}`"
                    class="fallback-image"
                    ref="fallbackImage"
                >
            </video>

            <div v-if="!isMediaLoaded" class="loading-indicator">
                <div class="loader"></div>
            </div>

            <button @click="handleFullClick" class="full-size radius-circle">
                <i class="icon-expand"></i>
            </button>
            <div class="controls">
                <button @click="prevMedia">
                    <i class="icon-arrow_left"></i>
                </button>
                <button
                    @click="handlePlayClick"
                    v-if="!!currentMedia?.url"
                    class="control-play"
                ><i :class="isPlaying ? 'icon-pause' : 'icon-play'"></i></button>
                <button @click="nextMedia">
                    <i class="icon-arrow_right"></i>
                </button>
            </div>
        </div>
    </div>
    <GalleryModal
        :externalMediaList="medias"
        :current="currentIndex"
        :isOpen="galleryOpen"
        @close="galleryOpen = false"
    ></GalleryModal>
</template>

<script>

import GalleryModal from "@/BinaryFlow/components/Product/Gallery/Gallery.vue";

const GALLERY_ITEMS = '.gallery-items';
const GALLERY_ITEM  = '.gallery-item';
const MAIN_MEDIA    = '.main-media';

import { defineComponent } from 'vue'
export default defineComponent({
    name: "gallery",
    components: {GalleryModal},
    data() {
        return {
            currentIndex: 0,
            medias: (window.videos || []).concat( window.images || [] ),
            isMediaLoaded: false,
            mediaCache: {}, // Кэширование медиа
            loading: false,
            isPlaying: false,

            mainMedia: null,
            galleryItems: null,
            galleryItemAll: null,

            fullSize: false,

            galleryOpen: false,
        }
    },
    computed: {
        currentMedia() {
            return ( this.medias || [] )[ this.currentIndex ] || {}
        },
        mediaCount() {
            return this.medias.length
        }
    },
    mounted() {

        this.mainMedia      = document.querySelector( MAIN_MEDIA );
        this.galleryItems   = document.querySelector( GALLERY_ITEMS );
        this.galleryItemAll = document.querySelectorAll( GALLERY_ITEM );

        this.loadMedias();
        this.updateMedia();

        // Вызываем функцию при загрузке страницы и при изменении размера окна
        window.onload = this.adjustGalleryHeight;
        window.onresize = this.adjustGalleryHeight;

        this.scrollToActive()
    },
    beforeDestroy() {
        this.clearCache()
    },
    methods: {
        scrollToActive() {
            // активный элемент
            const activeItem = document.querySelector(GALLERY_ITEMS + ' .active');
            if (!activeItem) return;

            // позиции элементов
            const rect = activeItem.getBoundingClientRect();
            const containerRect = this.galleryItems.getBoundingClientRect();

            // Проверяем видимость элемента
            const isTopVisible = rect.top >= 0;
            const isBottomVisible = rect.bottom <= containerRect.height;

            // Рассчитываем необходимое смещение
            let scrollTop = this.galleryItems.scrollTop;

            if ( ! isTopVisible) {
                // Элемент выше видимой области
                scrollTop = 0
            } else if (!isBottomVisible) {
                // Элемент ниже видимой области
                scrollTop = this.galleryItems.scrollTop + rect.bottom - containerRect.height; // отступы
            }

            // Плавная прокрутка
            this.galleryItems.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
            });
        },
        // Устанавливаем высоту галереи равной высоте основного изображения
        adjustGalleryHeight() {
            this.galleryItems.style.maxHeight = `${this.mainMedia.clientHeight}px`;
        },
        // Навигация
        changeMedia(index) {
            this.currentIndex = index
            this.isMediaLoaded = false
            this.updateMedia()

            // Добавляем вызов воспроизведения для видео
            if( !!this.currentMedia?.url ) {
                this.$nextTick(() => {
                    this.playVideo();
                });
            } else {
                this.videoPause();
            }
        },
        prevMedia() {
            if( ! this.mediaCount ) return
            this.currentIndex = ( this.currentIndex + this.mediaCount - 1 ) % this.mediaCount
            this.updateMedia()
        },
        nextMedia() {
            if( ! this.mediaCount ) return
            this.currentIndex = ( this.currentIndex + 1 ) % this.mediaCount
            this.updateMedia()
        },
        // Плавная смена медиа
        async updateMedia() {

            this.loading = true
            try {
                await this.loadCurrentMedia()
                this.isMediaLoaded = true
                if( !!this.currentMedia?.url ) {
                    await this.$nextTick(() => {
                        this.playVideo();
                    });
                } else {
                    this.videoPause();
                }

                // поправляем миниатюру чтобы она была в области видимости
                this.scrollToActive()
            } catch (error) {
                console.error('Error when load media:', error)
                this.isMediaLoaded = false
            } finally {
                this.loading = false
            }
        },
        async loadCurrentMedia() {
            const media = this.currentMedia

            if( !!media?.large ) {
                const image = await this.loadImage(media.large)
                await this.$nextTick(() => {
                    this.$refs.mainImg.src =
                        image.src
                })
            } else if( !!media?.url ) {

                const video = this.$refs.mainVideo
                const fallbackImage = this.$refs.fallbackImage

                video.addEventListener('canplay', () => {
                    fallbackImage.style.display = 'none';
                    this.$nextTick(() => {
                        this.playVideo();
                    });
                });
                video.addEventListener('error', () => {
                    fallbackImage.style.display = 'block';
                });
            }
        },

        // Кэширование
        addToCache( url, media ) {
            if( Object.keys( this.mediaCache ).length >= 10) {
                const firstKey = Object.keys( this.mediaCache )[0]
                delete this.mediaCache[firstKey]
            }
            this.mediaCache[url] = media
        },
        clearCache() {
            this.mediaCache = {}
        },

        // Загрузка медиа
        async loadMedias() {
            const start = Math.max( 0, this.currentIndex - 2 )
            const end = Math.min( this.mediaCount - 1, this.currentIndex + 2 )

            for (let i = start; i <= end; i++) {
                const media = this.medias[i]
                if( !!media?.large )
                    await this.loadImage(media.large)
            }
        },
        async loadImage(url) {

            if( this.mediaCache[url] ) {
                return this.mediaCache[url]
            }

            return new Promise((resolve, reject) => {
                const image = new Image()
                image.src = url
                image.onload = () => {
                    this.addToCache(url, image)
                    resolve(image)
                }
                image.onerror = () => {
                    reject(new Error('Error when load media'))
                }
            })
        },
        mediaLoaded() {
            this.isMediaLoaded = true
        },
        // Обработка видео
        videoError() {
            console.error('Error when load media')
            this.isMediaLoaded = false
        },
        handleFullClick() {
            this.galleryOpen = true
        },
        handlePlayClick() {
            if( !!this.currentMedia?.url ) {
                if( this.$refs.mainVideo.paused ) {
                    this.playVideo();
                } else {
                    this.$refs.mainVideo.pause();
                    this.videoPause()
                }
            }
        },
        // Обработка событий видео
        videoPlay() {
            this.isPlaying = true;
        },
        videoPause() {
            this.isPlaying = false;
        },
        playVideo() {
            if( this.$refs.mainVideo ) {
                setTimeout(() => {
                    this.$refs.mainVideo.play()
                    this.videoPlay()
                }, 400)
            }
        }
    }
})
</script>

<style scoped>

</style>